import Image from 'next/image';
import { isFilled, type RichTextField } from '@prismicio/client';
import { PrismicRichText, type JSXMapSerializer } from '@prismicio/react';
import cn from 'classnames';

import { richTextHyperlink } from './hyperlink';
import { imageLinkAttributes } from './linkAttributes';

import styles from './RichText.module.scss';

export interface RichTextProps {
  field: RichTextField | null | undefined;
  className?: string;
  /** `sizes` for inline images. Pass the widest the container can render,
      so the browser doesn't fetch more pixels than it shows. */
  imageSizes?: string;
}

const DEFAULT_IMAGE_SIZES = '(min-width: 988px) 50vw, 100vw';

// Type is set on each element rather than inherited from the wrapper:
// `styles/base.scss` extends `%t-body` onto every `p`, which beats
// inherited type (see ED-340's quote fix).
const components: JSXMapSerializer = {
  heading2: ({ children }) => <h2 className={styles.heading2}>{children}</h2>,
  heading3: ({ children }) => <h3 className={styles.heading3}>{children}</h3>,
  heading4: ({ children }) => <h4 className={styles.heading4}>{children}</h4>,
  heading5: ({ children }) => <h5 className={styles.heading5}>{children}</h5>,
  heading6: ({ children }) => <h6 className={styles.heading6}>{children}</h6>,
  paragraph: ({ children }) => <p className={styles.paragraph}>{children}</p>,
  list: ({ children }) => <ul className={styles.list}>{children}</ul>,
  oList: ({ children }) => <ol className={styles.list}>{children}</ol>,
  listItem: ({ children }) => <li className={styles.listItem}>{children}</li>,
  oListItem: ({ children }) => <li className={styles.listItem}>{children}</li>,
  hyperlink: richTextHyperlink,
};

const imageComponent =
  (sizes: string): JSXMapSerializer['image'] =>
  ({ node }) => {
    const image = (
      <Image
        className={styles.image}
        src={node.url}
        // Prismic stores a missing alt as null; empty marks it decorative.
        alt={node.alt ?? ''}
        width={node.dimensions.width}
        height={node.dimensions.height}
        sizes={sizes}
      />
    );
    // The image's own Link field, set in the image card in Prismic.
    const link = imageLinkAttributes(node);

    return link ? (
      <a className={styles.imageLink} {...link}>
        {image}
      </a>
    ) : (
      image
    );
  };

/**
 * Renders a Prismic rich text field with the design system's type for
 * h2–h6, paragraphs, lists, links and inline images (ED-249). Shared by
 * any component that takes free-form CMS copy; renders nothing when the
 * field is empty.
 */
const RichText = ({
  field,
  className,
  imageSizes = DEFAULT_IMAGE_SIZES,
}: RichTextProps) => {
  if (!isFilled.richText(field)) return null;

  return (
    <div className={cn(styles.root, className)}>
      <PrismicRichText
        field={field}
        components={{ ...components, image: imageComponent(imageSizes) }}
      />
    </div>
  );
};

export default RichText;
